/**
 * Traceability event model (plan 4.11) and data export / contract-end
 * deletion (4.12).
 */

const en = {
  stock: {
    trace: {
      chain: "Multi-level chain",
      upstream: "Upstream (inputs)",
      downstream: "Downstream (outputs)",
      depth: "Level {{depth}}",
      source: { events: "From trace events", records: "From business records" },
      recordsHint: "No trace events exist yet for this lot; the report was built from the business records. Run the backfill job to see the multi-level chain.",
      keyKinds: { lot: "Lot", lotno: "Lot no.", serial: "Serial", sscc: "SSCC", pkg: "Package", plt: "Pallet", product: "Product" },
      linkKinds: { transformation: "transformation", merge: "merge", split: "split", aggregation: "packed into" },
      stages: { transfer: "Transfer" },
      gln: "GLN (GS1 location number)",
      glnHint: "13 digits with a valid check digit; used as the location of trace events.",
      glnInvalid: "The GLN must be 13 digits with a valid check digit."
    }
  },
  admin: {
    portability: {
      tab: "Data portability",
      export: {
        title: "Export all data",
        description: "Download your records, users and audit log as JSON, CSV or XLSX together with your files in a single package. The package contains a manifest and SHA-256 checksums for every entry.",
        formats: "Formats",
        format: { json: "JSON", csv: "CSV", xlsx: "Excel (XLSX)" },
        includeFiles: "Include stored files",
        filesHint: "Files are downloaded in the browser; files that cannot be downloaded are listed in the manifest.",
        scopeHint: "This export contains what your account can read. The complete company export (every user's records and files) is prepared by support as a server job.",
        run: "Build and download package",
        phase: {
          records: "Reading records ({{done}}/{{total}})",
          files: "Downloading files ({{done}}/{{total}})",
          package: "Building the package"
        },
        failed: "The export could not be completed. Please try again.",
        done: "Package downloaded: {{fileName}}",
        totals: "{{records}} records in {{collections}} collections, {{files}} files.",
        skipped: "{{count}} files were not included.",
        skippedList: "Download the list",
        readErrors: "Could not be read: {{list}}"
      },
      deletion: {
        title: "Contract-end deletion",
        description: "Permanently deletes all company data, users and files. A second administrator must approve the request; deletion starts {{days}} days after approval at the earliest and can be cancelled until then. A signed deletion certificate (PDF) is issued.",
        cols: { created: "Requested", requestedBy: "Requested by", reason: "Reason", status: "Status", scheduledFor: "Deletion date", actions: "Actions" },
        state: {
          pending: "Awaiting second approval",
          scheduled: "Approved, scheduled",
          due: "Due",
          running: "Running",
          cancelled: "Cancelled",
          completed: "Completed",
          failed: "Failed"
        },
        approve: "Approve deletion",
        cancel: "Cancel request",
        waitingSecond: "Another administrator must approve.",
        certificates: "Deletion certificates",
        certificateLine: "{{documents}} documents deleted",
        reason: "Reason",
        reasonHint: "At least {{min}} characters, e.g. the contract end date.",
        exportConfirmed: "I have exported and checked the data I need to keep.",
        request: "Request deletion",
        approveTitle: "Approve contract-end deletion",
        approveMessage: "All company data will be deleted permanently in {{days}} days. Until then either administrator can cancel.",
        cancelTitle: "Cancel deletion request",
        cancelMessage: "The deletion request will be cancelled and no data will be deleted."
      },
      errors: {
        DELETION_REASON_TOO_SHORT: "Please enter a reason of at least 10 characters.",
        DELETION_EXPORT_NOT_CONFIRMED: "Confirm that you have exported your data first.",
        DELETION_ALREADY_OPEN: "There is already an open deletion request.",
        DELETION_SAME_APPROVER: "The request must be approved by a different administrator.",
        DELETION_NOT_PENDING: "The request is no longer awaiting approval.",
        DELETION_NOT_CANCELLABLE: "The request can no longer be cancelled.",
        generic: "The action failed. Please try again."
      }
    }
  }
};

const tr = {
  stock: {
    trace: {
      chain: "Çok seviyeli zincir",
      upstream: "Geriye (girdiler)",
      downstream: "İleriye (çıktılar)",
      depth: "Seviye {{depth}}",
      source: { events: "İzlenebilirlik olaylarından", records: "İş kayıtlarından" },
      recordsHint: "Bu lot için henüz izlenebilirlik olayı yok; rapor iş kayıtlarından oluşturuldu. Çok seviyeli zinciri görmek için geriye dönük olay üretim işini çalıştırın.",
      keyKinds: { lot: "Lot", lotno: "Lot no.", serial: "Seri", sscc: "SSCC", pkg: "Paket", plt: "Palet", product: "Ürün" },
      linkKinds: { transformation: "dönüşüm", merge: "birleştirme", split: "bölme", aggregation: "içine paketlendi" },
      stages: { transfer: "Transfer" },
      gln: "GLN (GS1 lokasyon numarası)",
      glnHint: "Geçerli kontrol basamaklı 13 hane; izlenebilirlik olaylarında yer bilgisi olarak kullanılır.",
      glnInvalid: "GLN, geçerli kontrol basamağı olan 13 haneli bir numara olmalı."
    }
  },
  admin: {
    portability: {
      tab: "Veri taşınabilirliği",
      export: {
        title: "Tüm verileri dışa aktar",
        description: "Kayıtlarınızı, kullanıcılarınızı ve denetim kaydınızı JSON, CSV veya XLSX olarak, dosyalarınızla birlikte tek pakette indirin. Pakette bir manifest ve her öğenin SHA-256 sağlama toplamı bulunur.",
        formats: "Biçimler",
        format: { json: "JSON", csv: "CSV", xlsx: "Excel (XLSX)" },
        includeFiles: "Saklanan dosyaları ekle",
        filesHint: "Dosyalar tarayıcıda indirilir; indirilemeyen dosyalar manifestte listelenir.",
        scopeHint: "Bu dışa aktarım hesabınızın okuyabildiği verileri içerir. Şirketin tamamı (tüm kullanıcıların kayıt ve dosyaları) destek ekibince sunucu işiyle hazırlanır.",
        run: "Paketi oluştur ve indir",
        phase: {
          records: "Kayıtlar okunuyor ({{done}}/{{total}})",
          files: "Dosyalar indiriliyor ({{done}}/{{total}})",
          package: "Paket oluşturuluyor"
        },
        failed: "Dışa aktarım tamamlanamadı. Lütfen tekrar deneyin.",
        done: "Paket indirildi: {{fileName}}",
        totals: "{{collections}} koleksiyonda {{records}} kayıt, {{files}} dosya.",
        skipped: "{{count}} dosya pakete eklenemedi.",
        skippedList: "Listeyi indir",
        readErrors: "Okunamayanlar: {{list}}"
      },
      deletion: {
        title: "Sözleşme sonu silme",
        description: "Şirketin tüm verilerini, kullanıcılarını ve dosyalarını kalıcı olarak siler. Talebi ikinci bir yönetici onaylamalıdır; silme onaydan en erken {{days}} gün sonra başlar ve o zamana kadar iptal edilebilir. İmzalı bir silme tutanağı (PDF) düzenlenir.",
        cols: { created: "Talep tarihi", requestedBy: "Talep eden", reason: "Gerekçe", status: "Durum", scheduledFor: "Silme tarihi", actions: "İşlemler" },
        state: {
          pending: "İkinci onay bekleniyor",
          scheduled: "Onaylandı, planlandı",
          due: "Vadesi geldi",
          running: "Çalışıyor",
          cancelled: "İptal edildi",
          completed: "Tamamlandı",
          failed: "Başarısız"
        },
        approve: "Silmeyi onayla",
        cancel: "Talebi iptal et",
        waitingSecond: "Başka bir yöneticinin onayı gerekiyor.",
        certificates: "Silme tutanakları",
        certificateLine: "{{documents}} belge silindi",
        reason: "Gerekçe",
        reasonHint: "En az {{min}} karakter; örneğin sözleşme bitiş tarihi.",
        exportConfirmed: "Saklamam gereken verileri dışa aktardım ve kontrol ettim.",
        request: "Silme talebi oluştur",
        approveTitle: "Sözleşme sonu silmeyi onayla",
        approveMessage: "Şirketin tüm verileri {{days}} gün sonra kalıcı olarak silinecek. O zamana kadar iki yönetici de iptal edebilir.",
        cancelTitle: "Silme talebini iptal et",
        cancelMessage: "Silme talebi iptal edilecek ve hiçbir veri silinmeyecek."
      },
      errors: {
        DELETION_REASON_TOO_SHORT: "Lütfen en az 10 karakterlik bir gerekçe girin.",
        DELETION_EXPORT_NOT_CONFIRMED: "Önce verilerinizi dışa aktardığınızı onaylayın.",
        DELETION_ALREADY_OPEN: "Zaten açık bir silme talebi var.",
        DELETION_SAME_APPROVER: "Talep farklı bir yönetici tarafından onaylanmalıdır.",
        DELETION_NOT_PENDING: "Talep artık onay beklemiyor.",
        DELETION_NOT_CANCELLABLE: "Talep artık iptal edilemez.",
        generic: "İşlem başarısız oldu. Lütfen tekrar deneyin."
      }
    }
  }
};

const ar = {
  stock: {
    trace: {
      chain: "سلسلة متعددة المستويات",
      upstream: "إلى الخلف (المدخلات)",
      downstream: "إلى الأمام (المخرجات)",
      depth: "المستوى {{depth}}",
      source: { events: "من أحداث التتبع", records: "من سجلات العمل" },
      recordsHint: "لا توجد أحداث تتبع لهذه الدفعة بعد؛ أُنشئ التقرير من سجلات العمل. شغّل مهمة الإنشاء الرجعي لرؤية السلسلة متعددة المستويات.",
      keyKinds: { lot: "دفعة", lotno: "رقم الدفعة", serial: "رقم تسلسلي", sscc: "SSCC", pkg: "طرد", plt: "منصة نقالة", product: "منتج" },
      linkKinds: { transformation: "تحويل", merge: "دمج", split: "تقسيم", aggregation: "معبأ في" },
      stages: { transfer: "تحويل مخزني" },
      gln: "GLN (رقم موقع GS1)",
      glnHint: "13 رقمًا مع رقم تحقق صحيح؛ يُستخدم كموقع لأحداث التتبع.",
      glnInvalid: "يجب أن يتكون GLN من 13 رقمًا مع رقم تحقق صحيح."
    }
  },
  admin: {
    portability: {
      tab: "قابلية نقل البيانات",
      export: {
        title: "تصدير جميع البيانات",
        description: "نزّل سجلاتك ومستخدميك وسجل التدقيق بصيغة JSON أو CSV أو XLSX مع ملفاتك في حزمة واحدة. تحتوي الحزمة على بيان ومجموع تحقق SHA-256 لكل عنصر.",
        formats: "الصيغ",
        format: { json: "JSON", csv: "CSV", xlsx: "Excel (XLSX)" },
        includeFiles: "تضمين الملفات المخزنة",
        filesHint: "تُنزَّل الملفات في المتصفح؛ تُدرج الملفات التي تعذر تنزيلها في البيان.",
        scopeHint: "يحتوي هذا التصدير على ما يمكن لحسابك قراءته. يُعدّ فريق الدعم تصدير الشركة الكامل (سجلات وملفات جميع المستخدمين) كمهمة على الخادم.",
        run: "إنشاء الحزمة وتنزيلها",
        phase: {
          records: "قراءة السجلات ({{done}}/{{total}})",
          files: "تنزيل الملفات ({{done}}/{{total}})",
          package: "إنشاء الحزمة"
        },
        failed: "تعذر إكمال التصدير. يرجى المحاولة مرة أخرى.",
        done: "تم تنزيل الحزمة: {{fileName}}",
        totals: "{{records}} سجل في {{collections}} مجموعة، {{files}} ملف.",
        skipped: "لم يُضمَّن {{count}} ملف.",
        skippedList: "تنزيل القائمة",
        readErrors: "تعذرت القراءة: {{list}}"
      },
      deletion: {
        title: "الحذف عند انتهاء العقد",
        description: "يحذف جميع بيانات الشركة ومستخدميها وملفاتها نهائيًا. يجب أن يوافق مسؤول ثانٍ على الطلب؛ يبدأ الحذف بعد {{days}} أيام على الأقل من الموافقة ويمكن إلغاؤه حتى ذلك الحين. يُصدر محضر حذف موقّع (PDF).",
        cols: { created: "تاريخ الطلب", requestedBy: "مقدم الطلب", reason: "السبب", status: "الحالة", scheduledFor: "تاريخ الحذف", actions: "الإجراءات" },
        state: {
          pending: "بانتظار الموافقة الثانية",
          scheduled: "تمت الموافقة، مجدول",
          due: "مستحق",
          running: "قيد التنفيذ",
          cancelled: "ملغى",
          completed: "مكتمل",
          failed: "فشل"
        },
        approve: "الموافقة على الحذف",
        cancel: "إلغاء الطلب",
        waitingSecond: "يجب أن يوافق مسؤول آخر.",
        certificates: "محاضر الحذف",
        certificateLine: "حُذف {{documents}} مستند",
        reason: "السبب",
        reasonHint: "{{min}} أحرف على الأقل، مثل تاريخ انتهاء العقد.",
        exportConfirmed: "لقد صدّرت البيانات التي أحتاج إلى الاحتفاظ بها وتحققت منها.",
        request: "طلب الحذف",
        approveTitle: "الموافقة على الحذف عند انتهاء العقد",
        approveMessage: "ستُحذف جميع بيانات الشركة نهائيًا بعد {{days}} أيام. حتى ذلك الحين يمكن لأي من المسؤولَين الإلغاء.",
        cancelTitle: "إلغاء طلب الحذف",
        cancelMessage: "سيُلغى طلب الحذف ولن تُحذف أي بيانات."
      },
      errors: {
        DELETION_REASON_TOO_SHORT: "يرجى إدخال سبب لا يقل عن 10 أحرف.",
        DELETION_EXPORT_NOT_CONFIRMED: "أكّد أولًا أنك صدّرت بياناتك.",
        DELETION_ALREADY_OPEN: "يوجد طلب حذف مفتوح بالفعل.",
        DELETION_SAME_APPROVER: "يجب أن يوافق على الطلب مسؤول مختلف.",
        DELETION_NOT_PENDING: "لم يعد الطلب بانتظار الموافقة.",
        DELETION_NOT_CANCELLABLE: "لم يعد من الممكن إلغاء الطلب.",
        generic: "فشل الإجراء. يرجى المحاولة مرة أخرى."
      }
    }
  }
};

export const portabilityLocales = { en, tr, ar };
